/**
 * Evaluating a rule, through the Rust engine.
 *
 * WHY THIS FILE CHANGED SHAPE. There were two engines: this package's
 * TypeScript evaluator and the Rust compiler behind `@crown/dsl-compiler`. They
 * agreed on 203 shared cases with dependencies, diagnostics and parse trees
 * byte-identical, and the plan's phase 4 is to keep one. This is the switch: a
 * rule is compiled by the Rust engine, and there is no second implementation to
 * disagree with it.
 *
 * The signature is unchanged, so the twelve call sites in this repository did
 * not move. What changed underneath is which engine answers.
 *
 * ## Compiled once, kept
 *
 * A compiled expression is a handle into the engine, and the same rule is
 * evaluated once per row of a dataset or once per keystroke in a form. The
 * cache is keyed by source text, as it was before, because that is what a
 * caller repeats.
 *
 * ## Programs
 *
 * A source containing `use`, `print`, `return` or `when` is a program rather
 * than an expression: it can name intermediate values and print. Detection is
 * unchanged - it lexes rather than matching a regular expression, so the word
 * `return` inside a string is not mistaken for a statement.
 *
 * ## Where the engine has to be ready
 *
 * On Node, Bun and Deno the engine is the native addon and is loaded on first
 * use. In a browser it is WebAssembly and loading it is asynchronous, so an
 * application must `await init()` from `@crown/dsl-compiler` once at start-up.
 * A page that has not will get a `DslError` naming that requirement rather than
 * a wrong answer: there is deliberately no TypeScript evaluator left to fall
 * back to, because a silent fallback is how two engines drift apart again.
 */
import { reviveCurrencies } from './currency.js';
import { traced } from './engine-debug.js';
import {
  compileProgram,
  createContext,
  declareInputs,
  DslError,
  isProgramSource,
  parse,
  type CompiledProgram,
  type ContextValue,
} from '@crown/dsl-compiler';

import type { ExpressionContext } from './ast.js';

/**
 * Every rule compiled as a program, by the source text a caller repeats.
 *
 * A rule is one line or many; either way the engine compiles and runs it as a
 * program - the one path every host takes - with its inputs declared in front
 * of it (`declareInputs`): `let root = sys.input("root")` for each of `root`,
 * `value` and `ctx` it reads, so everything it is given comes in through
 * `sys.input` while the people writing rules write them as they always have.
 */
const programCache = new Map<string, CompiledProgram>();

/** The engine's compiled form of a program, compiled once and kept. */
function compiledProgram(src: string): CompiledProgram {
  const cached = programCache.get(src);
  if (cached) return cached;
  // A rule that says nothing - only comments, or nothing at all - is a mistake
  // to report, though as a program it would compile and answer null.
  const tree = parse(src, true).ast;
  if (tree !== null && typeof tree === 'object' && 'stmts' in tree && Array.isArray(tree.stmts) && tree.stmts.length === 0)
    throw new DslError('the rule has nothing in it to evaluate');
  const compiled = traced('compile', src, false, () => compileProgram(declareInputs(src)));
  programCache.set(src, compiled);
  return compiled;
}

/** Whether a source has already been compiled, for what a debugger is told. */
function isHeld(source: string): boolean {
  return programCache.has(source);
}

/**
 * Run a compiled expression against a context.
 *
 * The context bag is passed only when there is one: without it the engine
 * evaluates against a value and a record directly, which needs no handle to
 * create and release.
 */
/**
 * A context's three parts, as the engine takes them.
 *
 * SAFETY: `ExpressionContext` types all three as `unknown` because a caller may
 * put any record in it, and every caller in this repository puts something
 * JSON-shaped there - a parsed document, a form's state, a bag of request
 * parameters. The engine serialises whatever it is given, so a value that is
 * not JSON-shaped would fail at that boundary rather than be misread here.
 */
interface EngineContext {
  value: ContextValue;
  root: ContextValue;
  /** Absent when the caller passed no bag, so no context handle is needed. */
  bag: ContextValue | undefined;
}

function engineContext(ctx: ExpressionContext<unknown, unknown>): EngineContext {
  // SAFETY: the member is declared `unknown` and every caller puts something
  // JSON-shaped there, which the engine serialises. See the note above.
  const value = (ctx.value ?? null) as ContextValue;
  // SAFETY: as `value` above.
  const root = (ctx.root ?? null) as ContextValue;
  // SAFETY: as `value` above.
  const bag = ctx.ctx === undefined ? undefined : (ctx.ctx as ContextValue);
  return { value, root, bag };
}

/** The inputs a rule is run with: `value`, `root` and, when there is one, `ctx`. */
function inputsOf(ctx: ExpressionContext<unknown, unknown>): Record<string, ContextValue> {
  const { value, root, bag } = engineContext(ctx);
  const inputs: Record<string, ContextValue> = { value, root };
  if (bag !== undefined) inputs.ctx = bag;
  return inputs;
}

/** A run's answer, with any money it holds made money again. */
function answerOf(value: ContextValue): ContextValue {
  // SAFETY: `Currency` carries the two fields the engine wrote, so it is that
  // object with two conversions added; `ContextValue` describes the same data.
  return reviveCurrencies(value) as ContextValue;
}

/**
 * Compile an expression into a reusable function.
 *
 * The returned function takes the same context object the evaluator always
 * took, so a caller that compiles once and runs per row is unchanged.
 *
 * @throws {DslError} if the expression does not compile, or - in a browser -
 * if the engine has not been initialised.
 */
export function compileExpression<Value = unknown, Root = unknown>(
  expr: string,
): (ctx: ExpressionContext<Value, Root>) => ContextValue {
  const compiled = compiledProgram(expr);
  // SAFETY: widening the two type parameters to `unknown`, which is what the
  // interface already declares the members as. Nothing is narrowed.
  return (ctx) => answerOf(compiled.run(inputsOf(ctx as ExpressionContext<unknown, unknown>)).value);
}

/**
 * Many rules against one record, with the record parsed once.
 *
 * WHY THIS EXISTS. `evaluateExpression` sends the whole record across the
 * engine boundary on every call: serialised here, parsed there, and every
 * number turned into the engine's decimal type. A form re-checks all its rules
 * on every keystroke, so an 85-field record was crossing that boundary dozens
 * of times per character typed, each time producing the same parsed record
 * again.
 *
 * The expression was already compiled once and kept. This is the other half:
 * the ARGUMENTS are parsed once too, and every rule evaluates against the same
 * parsed context. Measured over eight rules and an eighty-seven field record,
 * it is 3.9x the throughput of re-sending per rule.
 *
 * `setValue` is the cheap update a field edit wants: the value half is
 * replaced and the parsed root stays as it is.
 *
 * The handle owns memory outside the JavaScript heap and must be released,
 * which {@link withSession} does for you. A session is for one record: when the
 * record changes, take a new one.
 */
export interface EvaluationSession {
  /**
   * Evaluate one expression against this session's context.
   *
   * Compiled once and kept, exactly as `evaluateExpression` does - what is
   * saved here is the argument crossing, not the compile.
   */
  evaluate(expr: string): ContextValue;
  /** Replace the value half, keeping the parsed record. */
  setValue(value: ContextValue): void;
  /** Release the handle. Safe to call twice. */
  free(): void;
}

/**
 * Open a session over one record.
 *
 * @throws {DslError} if the engine has not been initialised - in a browser
 * that means `await init()` has not run yet.
 */
export function createSession(
  ctx: ExpressionContext<unknown, unknown>,
): EvaluationSession {
  const { value, root, bag } = engineContext(ctx);
  const handle = createContext(value, root, bag);
  let released = false;

  return {
    evaluate(expr: string): ContextValue {
      const held = isHeld(expr);
      return traced(
        'evaluate',
        expr,
        held,
        () => answerOf(compiledProgram(expr).runIn(handle).value),
        // What this expression can see, for a debugger that asked to keep it.
        () => ({ value, root, bag }),
      );
    },
    setValue(next: ContextValue): void {
      handle.setValue(next);
    },
    free(): void {
      if (released) return;
      released = true;
      handle.free();
    },
  };
}

/**
 * Run `work` against a session, releasing it afterwards even if `work` throws.
 *
 * The form to reach for. A handle that leaks holds memory the collector cannot
 * see, and under WebAssembly nothing reclaims it at all.
 */
export function withSession<T>(
  ctx: ExpressionContext<unknown, unknown>,
  work: (session: EvaluationSession) => T,
): T {
  const session = createSession(ctx);
  try {
    return work(session);
  } finally {
    session.free();
  }
}

/** What a program came to, and the lines it printed on the way. */
export interface ProgramRun {
  /** The program's `return` value. */
  value: ContextValue;
  /** Each `print` line, in the order the program wrote them. */
  output: string[];
}

/**
 * Run a program - a sequence of statements - and keep what it printed.
 *
 * `evaluateExpression` runs one too, but a program's output only reaches its
 * caller through a callback there. This returns the lines, which is what a
 * harness comparing two engines and a test asserting on `print` both need.
 *
 * @throws {DslError} if the program does not compile or fails while running.
 */
export function runProgram(
  src: string,
  ctx: { ctx: ExpressionContext<unknown, unknown> },
): ProgramRun {
  const result = compiledProgram(src).run(inputsOf(ctx.ctx));
  return { value: result.value, output: result.output.map((line) => String(line)) };
}

/**
 * Compile-and-evaluate an expression, caching the compiled form by source.
 *
 * Use this in hot paths - form field visibility that runs on every keystroke,
 * a computed column over a dataset. For a one-off where the source changes
 * every call, {@link compileExpression} avoids the cache entirely.
 *
 * A source containing program keywords runs as a program, and its `print`
 * output goes to `onOutput`. Unlike the evaluator this replaced, a program is
 * evaluated synchronously: the engine compiles and runs it in this call rather
 * than returning a promise. Awaiting the result is still correct, which is why
 * the callers that do so did not have to change.
 *
 * @param expr      The expression source, or a multi-statement program.
 * @param ctx       The runtime context (`value`, `root`, optional `ctx`).
 * @param onOutput  Where a program's `print` lines go. Defaults to the console.
 * @returns The value, or a program's `return`.
 * @throws {DslError} if it does not compile or fails while running.
 *
 * @example
 * evaluateExpression('value > 0', { value: 5, root: {} }); // → true
 */
export function evaluateExpression<Value = unknown, Root = unknown>(
  expr: string,
  ctx: ExpressionContext<Value, Root>,
  onOutput?: (line: string) => void,
): ContextValue {
  // SAFETY: widening the type parameters to what the interface already declares
  // its members as. See `engineContext` for what is assumed of the contents.
  const context = ctx as ExpressionContext<unknown, unknown>;

  const held = isHeld(expr);
  const write = onOutput ?? ((line: string) => console.log('[DSL]', line));
  // Traced around the whole run, printing included, so what a debugger is
  // told the rule came to is what the caller receives - and the time is the
  // time the caller waited.
  // Every rule runs as a program; a debugger still tells one line from many.
  return traced(
    isProgramSource(expr) ? 'program' : 'evaluate',
    expr,
    held,
    () => {
      const result = compiledProgram(expr).run(inputsOf(context));
      for (const line of result.output) {
        // A printed line is written as JSON, so a string keeps its quotes and a
        // record is readable rather than `[object Object]`.
        write(JSON.stringify(line) ?? String(line));
      }
      return answerOf(result.value);
    },
    () => engineContext(context),
  );
}
